import { useState, useEffect } from 'react';
import {
  Github,
  Linkedin,
  Youtube,
  Instagram,
  ExternalLink,
  Palette,
  Camera,
  Video,
  Link as LinkIcon
} from 'lucide-react';
import './App.css';

import coreJavaImage from './images/Core_Java.png';
import springBootImage from './images/spring-boot.png';
import springSecurityImage from './images/Spring_Security.png';
import javascriptImage from './images/javascript.png';
import htmlCssImage from './images/Html_Css.png';
import reactImage from './images/react.png';
import nextImage from './images/next.webp';
import figmaImage from './images/figma.png';
import premiereProImage from './images/Premiere_pro.png';
import afterEffectsImage from './images/Adobe_After_Effects.png';
import davinciResolveImage from './images/DaVinci_Resolve.png';
import canvaImage from './images/canva.webp';
import userImage from './images/user.png';
import genAITataImage from './images/GenAI_Tata.png';
import digitalSkillsAccentureImage from './images/DigitalSkills_Accenture.png';
import thynkImage from './images/Thynk.png';
import wtwImage from './images/WTW.jpg';
import campusCartImage from './images/CampusCart.png';
import greenLeaseImage from './images/GreenLease.jpeg';
import atsImage from './images/ATS.png';
import cloudIdsImage from './images/cloud-ids.png';
import repomindImage from './images/repomind.png';
import networkDefenseImage from './images/network_defense.png';
import pythonImage from './images/python.png';
import aimlImage from './images/AI.png';
import databaseImage from './images/database.png';
import fastapiImage from './images/fastapi.png';
import gitImage from './images/git.png';
import dockerImage from './images/docker.png';
import awsImage from './images/aws_icon.png';
import amazonImage from './images/amazon.png';
import oracleImage from './images/oracle.png';
import photography1 from './images/photography/1.jpg';
import photography2 from './images/photography/2.jpg';
import photography3 from './images/photography/3.jpg';
import photography4 from './images/photography/4.jpg';
import photography5 from './images/photography/5.jpg';
import photography6 from './images/photography/6.jpg';
import photography7 from './images/photography/7.jpg';
import photography8 from './images/photography/8.jpg';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections((prev) => new Set([...prev, entry.target.id]));
        } else {
          setVisibleSections((prev) => {
            const newSet = new Set(prev);
            newSet.delete(entry.target.id);
            return newSet;
          });
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.fade-section');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const projects = [
    {
      name: 'RepoMind AI – Multi-Agent Repository Reviewer',
      description: 'RepoMind AI is an automated repository auditing platform built with FastAPI and Next.js using MCP-driven AI agents. It evaluates security, code quality, architecture, and DevOps for public/private GitHub repos and delivers actionable reports.',
      tags: ['Next JS', 'FastAPI', 'AI', 'MCP', 'LangGraph', 'Gemini API'],
      githubLink: 'https://github.com/yuvashree031/RepoMind-AI-Multi-Agent-Repository-Reviewer',
      liveLink: 'https://repo-mind-ai-multi-agent-repository.vercel.app/',
      image: repomindImage,
      imageClass: 'project-image-repomind'
    },
    {
      name: 'AI-Powered ATS Resume Analyzer with RAG',
      description: 'An ATS application that analyzes resumes and compares them with job descriptions using AI, ranks candidates based on skills, keywords, and overall relevance, and provides clear feedback to help improve resumes and increase selection chances.',
      tags: ['Python', 'RAG', 'Langchain', 'AI'],
      githubLink: 'https://github.com/yuvashree031/AI-Powered-ATS-Resume-Analyzer-with-RAG',
      image: atsImage,
      imageClass: 'project-image-ats'
    },
    {
      name: 'Cloud Intrusion Detection System in AWS environment',
      description: 'A web-based intrusion detection system for monitoring AWS cloud environments. Detects real-time security threats using CloudTrail logs, IAM services, and features real-time alerts, threat scoring, and manual scanning capabilities.',
      tags: ['Python', 'Cloud', 'AWS'],
      githubLink: 'https://github.com/yuvashree031/Cloud-Intrusion-Detection-System',
      image: cloudIdsImage
    },
    {
      name: 'Thynk AI - Interactive Learning Platform',
      description: 'Built a scalable learning platform with an AI-powered chatbot for personalized Q&A, secure cloud-based storage, real-time data synchronization, and automated quiz generation to enhance student engagement.',
      tags: ['AI', 'Cloud', 'Real-time'],
      githubLink: 'https://github.com/yuvashree031/Thynk-AI---Interactive-Learning-Platform',
      image: thynkImage
    },
    {
      name: 'Self Healing Network Defense Bot',
      description: 'Developed an intelligent network defense system that uses machine learning and Q-learning to detect intrusions and autonomously respond to cyber threats in real time. Simulated attacks using Kali Linux and Ubuntu virtual machines on VMware to train, evaluate, and enhance adaptive network defense strategies.',
      tags: ['VM ware', 'Nmap', 'hping', 'Reinforcement Learning', 'Machine Learning'],
      githubLink: 'https://github.com/yuvashree031/RL-Network-Defense-Bot',
      image: networkDefenseImage,
      imageClass: 'project-image-defense'
    },
    {
      name: 'Waste To Worth',
      description: 'Built an Android app that acts as a bridge between donors and recipients by enabling real-time donation listings, location-based matching, and secure user authentication for a seamless donation experience.',
      tags: ['Android', 'Real-time', 'Location-based'],
      downloadLink: 'https://drive.google.com/file/d/1Ylcup08C1F2KFEFK13QOAQa3_EZp7yEW/view?usp=sharing',
      githubLink: 'https://github.com/yuvashree031/Waste-to-Worth-Android-App-',
      image: wtwImage,
      imageClass: 'project-image-wtw'
    },
    {
      name: 'Campus Cart',
      description: 'Developed a campus food ordering system that streamlines orders across multiple college canteens, helping reduce queues and wait times while improving the overall dining experience for students.',
      tags: ['Web App', 'Food Tech', 'Campus'],
      githubLink: 'https://github.com/yuvashree031/Campus-Cart',
      image: campusCartImage,
      imageClass: 'project-image-campus'
    },
    {
      name: 'GreenLease - Eco Friendly Homes',
      description: 'An eco-friendly home rental platform promoting sustainable living through green housing solutions, energy-efficient properties, and environmentally conscious community building.',
      tags: ['Sustainability', 'Web App', 'Green Tech'],
      githubLink: 'https://github.com/yuvashree031/GreenLease-Eco-Friendly-Homes',
      image: greenLeaseImage,
      imageClass: 'project-image-greenlease'
    }
  ];

  const technicalSkills = [
    { name: 'Python', image: pythonImage },
    { name: 'Core Java', image: coreJavaImage },
    { name: 'AI & ML', image: aimlImage },
    { name: 'Git & GitHub', image: gitImage },
    { name: 'FastAPI', image: fastapiImage },
    { name: 'AWS', image: awsImage },
    { name: 'Docker', image: dockerImage },
    { name: 'SQL & NoSQL', image: databaseImage },
    { name: 'Spring Boot', image: springBootImage },
    { name: 'Spring Security', image: springSecurityImage },
    { name: 'React JS', image: reactImage },
    { name: 'Next JS', image: nextImage },
    { name: 'JavaScript', image: javascriptImage },
    { name: 'HTML & CSS', image: htmlCssImage }
  ];

  const nonTechnicalSkills = [
    { name: 'Figma', image: figmaImage },
    { name: 'Adobe Premiere Pro', image: premiereProImage },
    { name: 'After Effects', image: afterEffectsImage },
    { name: 'DaVinci Resolve', image: davinciResolveImage },
    { name: 'Canva', image: canvaImage }
  ];

  const interests = [
    { name: 'UI / Web Design', icon: <Palette size={24} /> },
    { name: 'Photography', icon: <Camera size={24} /> },
    { name: 'Video Editing', icon: <Video size={24} /> }
  ];

  const certifications = [
    {
      title: 'AWS Certified Cloud Practitioner (CLF-C02)',
      link: 'https://www.credly.com/badges/ebbfe3ef-1a93-4dd1-b799-f7af56ccc42f/public_url',
      image: awsImage
    },
    {
      title: 'Oracle Cloud Infrastructure 2025 Certified AI Foundations Associate',
      link: 'https://catalog-education.oracle.com/ords/certview/sharebadge?id=64713680885426A784939E7BDE92C4FC7BE046ECDDE25362D396A921502E6B11',
      image: oracleImage
    },
    {
      title: 'GenAI - Powered Data Analytics Job Simulation',
      link: 'https://forage-uploads-prod.s3.amazonaws.com/completion-certificates/ifobHAoMjQs9s6bKS/gMTdCXwDdLYoXZ3wG_ifobHAoMjQs9s6bKS_kFRA6E6vBWFT7ZYvJ_1749892080560_completion_certificate.pdf',
      image: genAITataImage
    },
    {
      title: 'Digital Skills: User Experience - Accenture',
      link: 'https://www.futurelearn.com/certificates/rke5l0l',
      image: digitalSkillsAccentureImage
    },
    {
      title: 'Full Stack Web Development – Amazon - Coursera',
      link: 'https://bit.ly/3QrrMbw',
      image: amazonImage
    }
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: <Linkedin size={20} />, url: 'https://www.linkedin.com/in/yuvashree-r31', color: '#0077b5' },
    { name: 'GitHub', icon: <Github size={20} />, url: 'https://github.com/yuvashree031', color: '#333' },
    { name: 'LinkTree', icon: <LinkIcon size={20} />, url: 'https://linktr.ee/YuvashreeR31', color: '#39e09b' },
    {
      name: 'LeetCode',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M13.483 0a1.374 1.374 0 0 0-.961.438L7.116 6.226l-3.854 4.126a5.266 5.266 0 0 0-1.209 2.104 5.35 5.35 0 0 0-.125.513 5.527 5.527 0 0 0 .062 2.362 5.83 5.83 0 0 0 .349 1.017 5.938 5.938 0 0 0 1.271 1.818l4.277 4.193.039.038c2.248 2.165 5.852 2.133 8.063-.074l2.396-2.392c.54-.54.54-1.414.003-1.955a1.378 1.378 0 0 0-1.951-.003l-2.396 2.392a3.021 3.021 0 0 1-4.205.038l-.02-.019-4.276-4.193c-.652-.64-.972-1.469-.948-2.263a2.68 2.68 0 0 1 .066-.523 2.545 2.545 0 0 1 .619-1.164L9.13 8.114c1.058-1.134 3.204-1.27 4.43-.278l3.501 2.831c.593.48 1.461.387 1.94-.207a1.384 1.384 0 0 0-.207-1.943l-3.5-2.831c-.8-.647-1.766-1.045-2.774-1.202l2.015-2.158A1.384 1.384 0 0 0 13.483 0zm-2.866 12.815a1.38 1.38 0 0 0-1.38 1.382 1.38 1.38 0 0 0 1.38 1.382H20.79a1.38 1.38 0 0 0 1.38-1.382 1.38 1.38 0 0 0-1.38-1.382z" />
        </svg>
      ),
      url: 'https://leetcode.com/u/yuvashree31/',
      color: '#ffa116'
    },
    { name: 'YouTube', icon: <Youtube size={20} />, url: 'https://www.youtube.com/@yuv4sthetic', color: '#ff0000' },
    { name: 'Instagram', icon: <Instagram size={20} />, url: 'https://www.instagram.com/yuvas.05/reels/', color: '#e4405f' }
  ];

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">Yuvashree R</div>
          <div className="nav-links">
            <a href="#about">About</a>
            <a href="#tech-stack">Tech Stack</a>
            <a href="#projects">Projects</a>
            <a href="#skills">Skills</a>
            <a href="#interests">Interests</a>
            <a href="#certifications">Certifications</a>
            <a href="#problem-solving">Problem Solving</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" id="about">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              <span className="hi-text">Hi, I'm</span> <span className="gradient-text">Yuvashree</span>
            </h1>
            <p className="hero-subtitle">Software Engineering Student at VIT | Java | Python | AI & ML | NLP | Generative AI | Rest APIs | Web Development | Photography</p>
            <p className="hero-description">
              Final-year Integrated M.Tech Software Engineering student at VIT Vellore with strong skills in Java, Python, SQL, and the Spring Framework. Experienced in Git, Docker, CI/CD, AWS, and AI/ML, with a passion for building scalable, secure applications that solve real-world problems.
            </p>
            <div className="hero-buttons">
              <a href="#contact" className="btn btn-primary">Get In Touch</a>
              <a href="#projects" className="btn btn-secondary">
                View Projects
              </a>
              <a href={`${process.env.PUBLIC_URL}/Yuvashree_Resume.pdf`} target="_blank" rel="noopener noreferrer" className="btn btn-download">View / Download CV</a>
            </div>
          </div>
          <div className="hero-image">
            <div className="avatar-container">
              <img src={userImage} alt="Yuvashree R" className="user-avatar" />
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className={`section section-tech-stack fade-section ${visibleSections.has('tech-stack') ? 'fade-in' : 'fade-out'}`} id="tech-stack">
        <div className="container">
          <h2 className="section-title">Technology Stack</h2>
          <div className="tech-stack-grid">
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">Languages</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">Java, Python, JavaScript, SQL</span>
              </p>
            </div>
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">Frontend</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">HTML, CSS, JavaScript, React JS, Next JS</span>
              </p>
            </div>
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">Backend</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">FastAPI, Spring Boot, REST APIs, JWT, OAuth, Spring Security, API Design</span>
              </p>
            </div>
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">Databases</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">MySQL, Oracle SQL, Firebase, MongoDB</span>
              </p>
            </div>
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">AI / ML</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">NLP, Generative AI, RAG, Vector Search, LangChain, LangGraph</span>
              </p>
            </div>
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">Tools & DevOps</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">Git, GitHub, CI/CD, Docker, Render, Vercel</span>
              </p>
            </div>
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">Cloud</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">AWS EC2, S3, IAM, Lightsail</span>
              </p>
            </div>
            <div className="tech-stack-card">
              <h3 className="tech-stack-category">Core Concepts</h3>
              <p className="tech-stack-description">
                <span className="tech-highlight">DSA, OOP, Operating Systems, Linux, API Integration, SDLC, Security</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Projects Section */}
      <section className={`section section-projects fade-section ${visibleSections.has('projects') ? 'fade-in' : 'fade-out'}`} id="projects">
        <div className="container">
          <div className="section-header-with-link">
            <div>
              <h2 className="section-title">Academic Projects</h2>
              <p className="section-subtitle">Projects focused on real-world applications</p>
            </div>
            <a
              href="https://github.com/yuvashree031"
              target="_blank"
              rel="noopener noreferrer"
              className="github-profile-link"
            >
              <Github size={20} />
              <span>View All Projects</span>
            </a>
          </div>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <div key={index} className="project-card" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="project-image-container">
                  <img src={project.image} alt={project.name} className={`project-image ${project.imageClass || ''}`} />
                </div>
                <div className="project-number">0{index + 1}</div>
                <h3 className="project-name">{project.name}</h3>
                <p className="project-description">{project.description}</p>
                <div className="project-tags">
                  {project.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="project-tag">{tag}</span>
                  ))}
                </div>
                <div className="project-links">
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-link-btn"
                  >
                    <Github size={18} />
                    View Source
                  </a>
                  {project.liveLink && (
                    <a
                      href={project.liveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link-btn"
                    >
                      <ExternalLink size={18} />
                      Open Website
                    </a>
                  )}
                  {project.downloadLink && (
                    <a
                      href={project.downloadLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-download-link"
                    >
                      Download the app here →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className={`section section-alt fade-section ${visibleSections.has('skills') ? 'fade-in' : 'fade-out'}`} id="skills">
        <div className="container">
          <h2 className="section-title">Technical Skills</h2>
          <p className="skills-subtitle">Core technologies I work with</p>
          <div className="modern-skills-grid">
            {technicalSkills.map((skill, index) => (
              <div key={index} className="modern-skill-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="skill-icon-wrapper">
                  <img src={skill.image} alt={skill.name} className="skill-icon" />
                </div>
                <span className="skill-name">{skill.name}</span>
              </div>
            ))}
          </div>

          <h2 className="section-title" style={{ marginTop: '4rem' }}>Design & Editing Tools</h2>
          <p className="skills-subtitle">Creative tools for visual storytelling</p>
          <div className="modern-skills-grid design-skills-grid">
            {nonTechnicalSkills.map((skill, index) => (
              <div key={index} className="modern-skill-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="skill-icon-wrapper">
                  <img src={skill.image} alt={skill.name} className="skill-icon" />
                </div>
                <span className="skill-name">{skill.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interests Section */}
      <section className={`section section-interests fade-section ${visibleSections.has('interests') ? 'fade-in' : 'fade-out'}`} id="interests">
        <div className="photography-roll-container">
          <div className="photography-roll">
            {[photography1, photography2, photography3, photography4, photography5, photography6, photography7, photography8].map((img, index) => (
              <img key={`roll-1-${index}`} src={img} alt={`Photography ${index + 1}`} className="roll-image" />
            ))}
            {[photography1, photography2, photography3, photography4, photography5, photography6, photography7, photography8].map((img, index) => (
              <img key={`roll-2-${index}`} src={img} alt={`Photography ${index + 1}`} className="roll-image" />
            ))}
          </div>
        </div>
        <div className="container interests-content">
          <h2 className="section-title">Other Interests</h2>
          <div className="interests-grid">
            {interests.map((interest, index) => (
              <div key={index} className="interest-card">
                <div className="interest-icon">{interest.icon}</div>
                <h3>{interest.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className={`section section-certifications fade-section ${visibleSections.has('certifications') ? 'fade-in' : 'fade-out'}`} id="certifications">
        <div className="container">
          <h2 className="section-title">Courses & Certifications</h2>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="cert-card">
                <div className="cert-image-container">
                  <img src={cert.image} alt={cert.title} className="cert-image" />
                </div>
                <h3>{cert.title}</h3>
                <a href={cert.link} target="_blank" rel="noopener noreferrer" className="cert-link">
                  View Certificate <ExternalLink size={16} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Solving Section */}
      <section className={`section section-problem-solving fade-section ${visibleSections.has('problem-solving') ? 'fade-in' : 'fade-out'}`} id="problem-solving">
        <div className="container">
          <h2 className="section-title">Problem Solving</h2>
          <p className="section-subtitle">Competitive programming and algorithmic challenges</p>
          <div className="problem-solving-grid">
            <a
              href="https://leetcode.com/u/yuvashree31/"
              target="_blank"
              rel="noopener noreferrer"
              className="problem-solving-card"
            >
              <div className="problem-solving-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                  <path d="M13.483 0a1.374 1.374 0 0 0-.961.438L7.116 6.226l-3.854 4.126a5.266 5.266 0 0 0-1.209 2.104 5.35 5.35 0 0 0-.125.513 5.527 5.527 0 0 0 .062 2.362 5.83 5.83 0 0 0 .349 1.017 5.938 5.938 0 0 0 1.271 1.818l4.277 4.193.039.038c2.248 2.165 5.852 2.133 8.063-.074l2.396-2.392c.54-.54.54-1.414.003-1.955a1.378 1.378 0 0 0-1.951-.003l-2.396 2.392a3.021 3.021 0 0 1-4.205.038l-.02-.019-4.276-4.193c-.652-.64-.972-1.469-.948-2.263a2.68 2.68 0 0 1 .066-.523 2.545 2.545 0 0 1 .619-1.164L9.13 8.114c1.058-1.134 3.204-1.27 4.43-.278l3.501 2.831c.593.48 1.461.387 1.94-.207a1.384 1.384 0 0 0-.207-1.943l-3.5-2.831c-.8-.647-1.766-1.045-2.774-1.202l2.015-2.158A1.384 1.384 0 0 0 13.483 0zm-2.866 12.815a1.38 1.38 0 0 0-1.38 1.382 1.38 1.38 0 0 0 1.38 1.382H20.79a1.38 1.38 0 0 0 1.38-1.382 1.38 1.38 0 0 0-1.38-1.382z" />
                </svg>
              </div>
              <div className="problem-solving-content">
                <h3 className="problem-solving-title">LeetCode</h3>
                <p className="problem-solving-description">Solved over 90 problems focusing on core DSA concepts including arrays, strings, trees, graphs, and dynamic programming</p>
              </div>
              <div className="problem-solving-arrow">→</div>
            </a>

            <a
              href="https://www.hackerrank.com/profile/yuvashree_ra05"
              target="_blank"
              rel="noopener noreferrer"
              className="problem-solving-card"
            >
              <div className="problem-solving-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                  <path d="M12 0c1.285 0 9.75 4.886 10.392 6 .645 1.115.645 10.885 0 12S13.287 24 12 24s-9.75-4.885-10.395-6c-.641-1.115-.641-10.885 0-12C2.25 4.886 10.715 0 12 0zm2.295 6.799c-.141 0-.258.115-.258.258v3.875H9.963V6.908h.701c.141 0 .254-.115.254-.258 0-.094-.049-.176-.123-.221L9.223 4.92c-.049-.063-.141-.109-.226-.109-.084 0-.16.045-.207.107L7.11 6.43c-.072.045-.12.126-.12.218 0 .143.113.258.255.258h.704l.008 10.035c0 .145.111.258.254.258h6.875c.141 0 .254-.115.254-.258v-9.281c0-.143-.113-.258-.254-.258h-2.791z" />
                </svg>
              </div>
              <div className="problem-solving-content">
                <h3 className="problem-solving-title">HackerRank</h3>
                <p className="problem-solving-description">Strengthened problem-solving skills by solving algorithmic challenges in both Java and Python on HackerRank.</p>
              </div>
              <div className="problem-solving-arrow">→</div>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className={`section section-contact fade-section ${visibleSections.has('contact') ? 'fade-in' : 'fade-out'}`} id="contact">
        <div className="container">
          <h2 className="section-title">Let's Connect</h2>
          <p className="contact-subtitle">Find me on these platforms</p>

          <div className="contact-email-row">
            <a href="mailto:yuvashree.ra05@gmail.com" className="email-link-with-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <span>yuvashree.ra05@gmail.com</span>
            </a>
          </div>

          <div className="social-links">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                style={{ '--hover-color': social.color }}
              >
                {social.icon}
                <span>{social.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>© 2026 Yuvashree R - Portfolio</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
